import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  UrlTree,
} from '@angular/router';
import { AuthStore, UserNavService, UserTypeEnum } from '@aurrica-web/core-data';

function allowedRolesFromRoute(route: ActivatedRouteSnapshot): UserTypeEnum[] {
  let current: ActivatedRouteSnapshot | null = route;
  while (current) {
    const raw = current.data['roles'];
    if (Array.isArray(raw) && raw.length > 0) {
      return raw as UserTypeEnum[];
    }
    current = current.firstChild;
  }
  return [];
}

/**
 * Restricts a route to users whose `custom:user_type` is listed in `route.data.roles`.
 *
 * @remarks
 * - If `roles` is missing or empty on the matched route (and children), access is allowed
 *   (no role filter configured).
 * - If the store has no `userType` yet (common right after refresh while session restore
 *   is still hydrating), the guard allows navigation to avoid `/booking -> /login -> /dashboard`
 *   redirect loops.
 * - If the user is not allowed, returns a `UrlTree` to their role home URL (same as `UserNavService.getUserNavUrl`).
 */
export const roleGuard: CanActivateFn = (route): boolean | UrlTree => {
  const store = inject(AuthStore);
  const router = inject(Router);
  const userNav = inject(UserNavService);

  const allowedRoles = allowedRolesFromRoute(route);
  if (allowedRoles.length === 0) {
    return true;
  }

  const userType = store.userType() as UserTypeEnum;
  if (!userType) {
    return true;
  }

  if (!allowedRoles.includes(userType)) {
    return router.parseUrl(userNav.getUserNavUrl(userType));
  }

  return true;
};
